/**
 * Email Campaigns - Email marketing with AI-generated content
 * Create broadcast emails, sequences, and automations
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { apiClient } from '../lib/api';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Icon, type IconName } from '../components/icons';

type CampaignType = 'broadcast' | 'sequence' | 'automation';
type CampaignStatus = 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused';

interface EmailCampaign {
  id: string;
  name: string;
  campaign_type: CampaignType;
  subject_line: string;
  preview_text: string;
  html_content: string;
  status: CampaignStatus;
  scheduled_at: number | null;
  sent_at: number | null;
  recipient_count: number;
  opens: number;
  clicks: number;
  created_at: number;
}

const CAMPAIGN_TYPES: { value: CampaignType; label: string; description: string; icon: IconName }[] = [
  {
    value: 'broadcast',
    label: 'Broadcast',
    description: 'Send to all subscribers at once',
    icon: 'send',
  },
  {
    value: 'sequence',
    label: 'Sequence',
    description: 'Drip campaign over time',
    icon: 'arrow-right',
  },
  {
    value: 'automation',
    label: 'Automation',
    description: 'Trigger-based emails',
    icon: 'sparkles',
  },
];

const EMAIL_TEMPLATES = [
  { id: 'welcome', name: 'Welcome Email', description: 'Greet new subscribers' },
  { id: 'promo', name: 'Promotional', description: 'Sales and offers' },
  { id: 'newsletter', name: 'Newsletter', description: 'Regular updates' },
  { id: 'announcement', name: 'Announcement', description: 'Product launches' },
  { id: 'followup', name: 'Follow-up', description: 'Re-engage subscribers' },
];

export function EmailCampaigns() {
  const { selectedBrand } = useBrand();
  const navigate = useNavigate();

  // Form state
  const [name, setName] = useState('');
  const [campaignType, setCampaignType] = useState<CampaignType>('broadcast');
  const [subjectLine, setSubjectLine] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [activeTab, setActiveTab] = useState<'create' | 'campaigns'>('create');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (selectedBrand) {
      // Load campaigns from localStorage (or API when implemented)
      loadCampaigns();
    }
  }, [selectedBrand]);

  const loadCampaigns = () => {
    // Load campaigns from localStorage
    const savedCampaigns = localStorage.getItem(`email_campaigns_${selectedBrand?.id}`);
    if (savedCampaigns) {
      try {
        setCampaigns(JSON.parse(savedCampaigns));
      } catch {
        setCampaigns([]);
      }
    } else {
      setCampaigns([]);
    }
  };

  const saveCampaigns = (updatedCampaigns: EmailCampaign[]) => {
    if (selectedBrand) {
      localStorage.setItem(`email_campaigns_${selectedBrand.id}`, JSON.stringify(updatedCampaigns));
    }
  };

  const handleGenerateContent = async () => {
    if (!selectedBrand) return;

    setIsGenerating(true);
    setError('');

    try {
      const response = await apiClient.generateCaption({
        brand_id: selectedBrand.id,
        prompt: `Write an email ${campaignType === 'broadcast' ? 'announcement' : campaignType} for a brand. Include a compelling subject line and body. Topic: ${name || 'general update'}. Make it engaging and actionable.`,
        platform: 'twitter', // Using twitter for shorter, punchier content
        tone: 'professional',
        length: 'long',
        include_hashtags: false,
        include_emoji: true,
      });

      if (response.success && response.data) {
        const content = response.data.caption;
        const lines = content.split('\n').filter((l: string) => l.trim());

        // Parse generated content
        if (lines.length >= 1) {
          setSubjectLine(lines[0].replace(/^(Subject:|RE:|FW:)/i, '').trim().substring(0, 100));
        }
        if (lines.length >= 2) {
          setPreviewText(lines[1].substring(0, 150));
        }
        if (lines.length >= 3) {
          setHtmlContent(lines.slice(1).join('\n\n'));
        }

        setSuccess('Email content generated! Customize as needed.');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to generate email content');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!selectedBrand || !name.trim() || !subjectLine.trim()) {
      setError('Please fill in campaign name and subject line');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const newCampaign: EmailCampaign = {
        id: `campaign-${Date.now()}`,
        name,
        campaign_type: campaignType,
        subject_line: subjectLine,
        preview_text: previewText,
        html_content: htmlContent,
        status: 'draft',
        scheduled_at: scheduledDate && scheduledTime
          ? new Date(`${scheduledDate}T${scheduledTime}`).getTime()
          : null,
        sent_at: null,
        recipient_count: 0,
        opens: 0,
        clicks: 0,
        created_at: Date.now(),
      };

      const updatedCampaigns = [newCampaign, ...campaigns];
      setCampaigns(updatedCampaigns);
      saveCampaigns(updatedCampaigns);
      setSuccess('Email campaign saved!');
      setTimeout(() => setSuccess(''), 3000);

      // Reset form
      setName('');
      setSubjectLine('');
      setPreviewText('');
      setHtmlContent('');
      setScheduledDate('');
      setScheduledTime('');
      setActiveTab('campaigns');
    } catch (err) {
      setError('Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setName(`${template.name} - ${new Date().toLocaleDateString()}`);
    }
  };

  const getOpenRate = (campaign: EmailCampaign) => {
    if (campaign.recipient_count === 0) return 0;
    return ((campaign.opens / campaign.recipient_count) * 100).toFixed(1);
  };

  const getClickRate = (campaign: EmailCampaign) => {
    if (campaign.opens === 0) return 0;
    return ((campaign.clicks / campaign.opens) * 100).toFixed(1);
  };

  const getStatusColor = (status: CampaignStatus) => {
    switch (status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'sending': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedBrand) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="text-center p-6">
          <p className="text-gray-500">Please select a brand from the dashboard</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Create and manage email marketing for{' '}
            <span className="font-medium" style={{ color: selectedBrand.color_primary }}>
              {selectedBrand.name}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={activeTab === 'create' ? 'primary' : 'outline'}
            onClick={() => setActiveTab('create')}
          >
            <Icon name="plus" size="sm" className="mr-2" />
            Create
          </Button>
          <Button
            variant={activeTab === 'campaigns' ? 'primary' : 'outline'}
            onClick={() => setActiveTab('campaigns')}
          >
            <Icon name="mail" size="sm" className="mr-2" />
            Campaigns ({campaigns.length})
          </Button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {activeTab === 'create' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Campaign Type */}
            <Card>
              <CardHeader title="Campaign Type" subtitle="Choose how you want to send your email" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {CAMPAIGN_TYPES.map(({ value, label, description, icon }) => (
                  <button
                    key={value}
                    onClick={() => setCampaignType(value)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      campaignType === value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon name={icon} size="lg" className={`mb-2 ${campaignType === value ? 'text-blue-600' : 'text-gray-400'}`} />
                    <h4 className={`font-medium ${campaignType === value ? 'text-blue-700' : 'text-gray-700'}`}>
                      {label}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">{description}</p>
                  </button>
                ))}
              </div>
            </Card>

            {/* Templates */}
            <Card>
              <CardHeader title="Start from Template" subtitle="Choose a template or start from scratch" />
              <div className="flex flex-wrap gap-2">
                {EMAIL_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template.id)}
                    className={`px-4 py-2 rounded-lg border-2 text-left transition-all ${
                      selectedTemplate === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className={`text-sm font-medium ${selectedTemplate === template.id ? 'text-blue-700' : 'text-gray-700'}`}>
                      {template.name}
                    </span>
                  </button>
                ))}
              </div>
            </Card>

            {/* Email Content */}
            <Card>
              <CardHeader
                title="Email Content"
                subtitle="Write your email or generate with AI"
                action={
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleGenerateContent}
                    isLoading={isGenerating}
                  >
                    <Icon name="sparkles" size="sm" className="mr-1" />
                    Generate
                  </Button>
                }
              />

              <div className="space-y-4">
                <Input
                  label="Campaign Name"
                  placeholder="e.g., Black Friday Sale Announcement"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />

                <Input
                  label="Subject Line"
                  placeholder="Make it compelling!"
                  value={subjectLine}
                  onChange={e => setSubjectLine(e.target.value)}
                  maxLength={100}
                />
                <p className="text-xs text-gray-500 -mt-2">{subjectLine.length}/100 characters</p>

                <Input
                  label="Preview Text"
                  placeholder="Text shown next to subject in inbox"
                  value={previewText}
                  onChange={e => setPreviewText(e.target.value)}
                  maxLength={150}
                />

                <Textarea
                  label="Email Body"
                  placeholder="Write your email content here..."
                  value={htmlContent}
                  onChange={e => setHtmlContent(e.target.value)}
                  rows={10}
                />
              </div>
            </Card>

            {/* Scheduling */}
            {campaignType === 'broadcast' && (
              <Card>
                <CardHeader title="Schedule" subtitle="Send now or schedule for later" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Date"
                    type="date"
                    value={scheduledDate}
                    onChange={e => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <Input
                    label="Time"
                    type="time"
                    value={scheduledTime}
                    onChange={e => setScheduledTime(e.target.value)}
                  />
                </div>
              </Card>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setActiveTab('campaigns')} className="w-full sm:w-auto">
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={handleSave}
                isLoading={isSaving}
                className="w-full sm:w-auto"
              >
                <Icon name="save" size="sm" className="mr-2" />
                Save Draft
              </Button>
              <Button className="w-full sm:w-auto">
                <Icon name="send" size="sm" className="mr-2" />
                {scheduledDate ? 'Schedule Send' : 'Send Now'}
              </Button>
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader title="Email Preview" />

              {/* Email preview mockup */}
              <div className="bg-gray-50 rounded-lg overflow-hidden">
                {/* Email header */}
                <div className="bg-white p-4 border-b">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: selectedBrand.color_primary }}
                    >
                      {selectedBrand.name[0]}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedBrand.name}</p>
                      <p className="text-xs text-gray-500">to me</p>
                    </div>
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {subjectLine || 'Your Subject Line'}
                  </h3>
                  {previewText && (
                    <p className="text-sm text-gray-500 mt-1">{previewText}</p>
                  )}
                </div>

                {/* Email body */}
                <div className="p-4 bg-white min-h-[200px]">
                  {htmlContent ? (
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {htmlContent}
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 py-8">
                      <Icon name="mail" size="3xl" className="mx-auto mb-2 opacity-50" />
                      <p>Your email content will appear here</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      ) : (
        /* Campaigns List */
        <div className="space-y-4">
          {/* Stats Overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="text-center p-4">
              <Icon name="mail" size="lg" className="mx-auto mb-2 text-blue-500" />
              <p className="text-2xl font-bold text-gray-900">{campaigns.length}</p>
              <p className="text-sm text-gray-500">Total Campaigns</p>
            </Card>
            <Card className="text-center p-4">
              <Icon name="users" size="lg" className="mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, c) => sum + c.recipient_count, 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Total Recipients</p>
            </Card>
            <Card className="text-center p-4">
              <Icon name="eye" size="lg" className="mx-auto mb-2 text-purple-500" />
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, c) => sum + c.opens, 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Total Opens</p>
            </Card>
            <Card className="text-center p-4">
              <Icon name="bar-chart" size="lg" className="mx-auto mb-2 text-orange-500" />
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.reduce((sum, c) => sum + c.clicks, 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Total Clicks</p>
            </Card>
          </div>

          {/* Campaign List */}
          {campaigns.length === 0 ? (
            <Card className="text-center py-12">
              <Icon name="mail" size="3xl" className="mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500 mb-4">No campaigns yet</p>
              <Button onClick={() => setActiveTab('create')}>
                <Icon name="plus" size="sm" className="mr-2" />
                Create Your First Campaign
              </Button>
            </Card>
          ) : (
            <Card>
              <div className="divide-y divide-gray-200">
                {campaigns.map(campaign => (
                  <div
                    key={campaign.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-gray-900">{campaign.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                          </span>
                          <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600 capitalize">
                            {campaign.campaign_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{campaign.subject_line}</p>

                        {/* Metrics */}
                        {campaign.status === 'sent' && (
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-gray-500">
                              <Icon name="users" size="sm" className="inline mr-1" />
                              {campaign.recipient_count.toLocaleString()}
                            </span>
                            <span className="text-green-600">
                              <Icon name="check" size="sm" className="inline mr-1" />
                              {getOpenRate(campaign)}% opens
                            </span>
                            <span className="text-blue-600">
                              <Icon name="bar-chart" size="sm" className="inline mr-1" />
                              {getClickRate(campaign)}% clicks
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Icon name="copy" size="sm" className="mr-1" />
                          Duplicate
                        </Button>
                        {campaign.status === 'draft' && (
                          <Button size="sm">
                            <Icon name="send" size="sm" className="mr-1" />
                            Send
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
